import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { Box, TableContainer, TableHead, TableRow, TableCell, TableBody, Table, Paper, useTheme, Pagination, Typography, TextField } from '@mui/material';
import { blue, grey } from '@mui/material/colors';

import RowTableMensualidad from '../SubComponents/Mensualidades/RowTableMensualidad';
import EmptyState from '../common/EmptyState';
import { getUniqueCashboxForMovement } from '../../utils/cashbox';
import { getMensualidadesListParams, MENSUALIDAD_FILTERS } from '../../utils/mensualidadFilters';
import { getTopSearchMatchIds } from '../../utils/searchHighlights';

import { useAuth } from '../../Auth/AuthContext';
const API_URL = process.env.REACT_APP_API_URL;

const getId = (value) => (value && typeof value === 'object' ? value.id : value);
const hasValidId = (value) => {
  const id = getId(value);
  return id !== null && id !== undefined && id !== '' && typeof id !== 'object';
};


const handleDeleteMovimiento = async (id) => {
  if (!hasValidId(id)) {
    console.warn("Mensualidad historica sin movimiento asociado. Se omite eliminacion de movimiento.", id);
    return;
  }
  const movimientoResponse = await axios.get(`${API_URL}/movimiento/${id}/`)
  const movimientoAEliminar = movimientoResponse.data
  const caja = await getUniqueCashboxForMovement(API_URL, id);

  // 4️⃣ Actualizar los valores de ingresos o egresos
  let total_ingresos = Number(caja.total_ingresos);
  let total_egresos = Number(caja.total_egresos);

  if (movimientoAEliminar.tipo.toLowerCase() === "ingreso") {
    total_ingresos -= Number(parseFloat(movimientoAEliminar.monto));
  } else if (movimientoAEliminar.tipo.toLowerCase() === "egreso") {
    total_egresos -= Number(parseFloat(movimientoAEliminar.monto));
  }
  
  await axios.delete(`${API_URL}/movimiento/${id}/`)

  // 4️⃣ Enviar TODOS los datos de la caja, incluyendo los nuevos valores
  await axios.put(`${API_URL}/cajas/${caja.id}/`, {
    nombre: caja.nombre,
    fecha_inicio: caja.fecha_inicio,
    dinero_base: caja.dinero_base,
    empleado: caja.empleado,
    total_ingresos: Number(total_ingresos.toFixed(2)), // Redondear a 2 decimales
    total_egresos: Number(total_egresos.toFixed(2)), // Redondear a 2 decimales
    fecha_fin: caja.fecha_fin,
    saldo_final: caja.saldo_final,
    estado: caja.estado,
  });    
};

async function actualizarReporteDiario(valorEliminado, fecha) {
  const response = await axios.get(`${API_URL}/reporte_diario/`);
  const reportes = response.data;

  try {
    const reporteExistente = reportes.find(r => r.fecha === fecha && r.tipo === "M-I");

    if (reporteExistente) {
      const nuevoValor = parseFloat(reporteExistente.valor) - Number(valorEliminado);

      await axios.put(`${API_URL}/reporte_diario/${reporteExistente.id}/`, { valor: nuevoValor });
    }
  } catch (error) {
    console.error("Error en la actualización del reporte:", error);
  }
}

async function actualizarReporteDiarioDelete(fecha) {
  const response = await axios.get(`${API_URL}/reporte_diario/`);
  const reportes = response.data;

  const reporteExistente = reportes.find(r => r.fecha === fecha && r.tipo === "M-V");
  if (reporteExistente) {
      await axios.put(`${API_URL}/reporte_diario/${reporteExistente.id}/`, { valor: Number(reporteExistente.valor) - 1 } );
  }
}

function Mensualidades({ createdTarget, onCreatedTargetConsumed }) {
  const { user } = useAuth();
  const areUserAdmin = user.rol === 'Administrador';

  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  // paginado y búsqueda manejados por el servidor
  const [data, setData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const deleteRequestsRef = useRef(new Set());
  const locatedHighlightRef = useRef(null);

  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [filter, setFilter] = useState('todos');

  const rowsPerPage = 30; // coincide con DefaultPagination en backend
  const highlightId = createdTarget?.targetSection === 'Mensualidades' ? createdTarget.createdId : null;

  useEffect(() => {
    if (!highlightId) return;
    locatedHighlightRef.current = null;
    setFilter('todos');
    setSearch('');
    setSearchInput('');
    setCurrentPage(1);
  }, [highlightId]);

  const fetchMensualidades = useCallback(async ({ showLoading = true, propagateError = false } = {}) => {
    if (showLoading) setLoading(true);
    setLoadError(false);
    try {
      const res = await axios.get(`${API_URL}/mensualidades/listado/`, {
        params: getMensualidadesListParams({ search, filter, page: currentPage, pageSize: rowsPerPage, highlightId }),
      });
      if (res.status !== 200) throw new Error('Error al cargar los datos');
      const total = res.data.count || 0;
      const pages = Math.ceil(total / rowsPerPage);
      setCount(total);
      setTotalPages(pages);
      setData(res.data.results || []);
      if (currentPage > Math.max(1, pages)) setCurrentPage(Math.max(1, pages));
    } catch (err) {
      console.error('Error al cargar los datos:', err);
      setLoadError(true);
      if (propagateError) throw err;
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [currentPage, search, filter, highlightId, rowsPerPage]);

  useEffect(() => {
    fetchMensualidades();
  }, [fetchMensualidades]);

  useEffect(() => {
    if (highlightId && !loading && currentPage === 1 && !search && filter === 'todos' && !data.some((item) => Number(item.id) === Number(highlightId))) {
      console.warn(`No se encontró la mensualidad recién creada con ID ${highlightId}.`);
      onCreatedTargetConsumed?.(highlightId);
    }
  }, [currentPage, data, filter, highlightId, loading, onCreatedTargetConsumed, search]);

  const handlePageChange = useCallback((event, value) => {
    setCurrentPage(value);
  }, []);

  const handleSearchChange = useCallback((e) => {
    setSearchInput(e.target.value);
  }, []);

  const handleSearchKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      setSearch(searchInput);
      setCurrentPage(1);
    }
  }, [searchInput]);

  const handleFilterChange = useCallback((value) => {
    setFilter(value);
    setCurrentPage(1);
  }, []);

  const handleDeleteMensualidad = useCallback(async (id, valor, fecha, idMovimiento, idsMovimientosRenovaciones) => {
    if (deleteRequestsRef.current.has(id)) return;
    deleteRequestsRef.current.add(id);
    try {
      await actualizarReporteDiarioDelete(fecha);
      await axios.delete(`${API_URL}/mensualidades/${id}/`);
      await handleDeleteMovimiento(idMovimiento);
      for (const idRenovacion of idsMovimientosRenovaciones) {
        await handleDeleteMovimiento(idRenovacion);
      }
      await actualizarReporteDiario(valor, fecha);
      await fetchMensualidades({ showLoading: false });
    } catch (error) {
      console.error('Error deleting mensualidad:', error);
    } finally {
      deleteRequestsRef.current.delete(id);
    }
  }, [fetchMensualidades]);

  const handleUpdateData = useCallback(() => {
    return fetchMensualidades({ showLoading: false, propagateError: true });
  }, [fetchMensualidades]);
  const handleHighlightLocated = useCallback((id) => {
    locatedHighlightRef.current = Number(id);
  }, []);

  const searchHighlightIds = useMemo(() => new Set(getTopSearchMatchIds(
    data,
    search,
    [
      'id',
      (item) => item.historial?.placa,
      (item) => item.cliente?.nombre,
      (item) => item.cliente?.apellidos,
      (item) => item.cliente?.documento,
    ]
  )), [data, search]);

  return loading ? (
    <Box sx={{ display: 'flex', justifyContent: 'center', paddingTop: 4 }}>
      <Typography variant="h6" color="textSecondary">
        Cargando datos...
      </Typography>
    </Box>
  ) : (
    <Box sx={{ paddingTop: 1 }}>
      <Typography variant="h5" color="grey" sx={{ paddingBottom: 2 }}>
        TOTAL DE MENSUALIDADES: {count}
      </Typography>
      {loadError && data.length > 0 && (
        <Typography color="error" sx={{ paddingBottom: 2 }}>
          No fue posible cargar las mensualidades.
        </Typography>
      )}

      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          size="small"
          label="Buscar mensualidad"
          value={searchInput}
          onChange={handleSearchChange}
          onKeyDown={handleSearchKeyDown}
        />
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        {MENSUALIDAD_FILTERS.map(([value, label, selectedColor]) => (
          <Box
            key={value}
            onClick={() => handleFilterChange(value)}
            sx={{
              px: 2,
              py: 1,
              borderRadius: 2,
              cursor: 'pointer',
              backgroundColor: filter === value ? selectedColor : '#8a8a8a',
              color: filter === value ? 'white' : 'black',
            }}
          >
            {label}
          </Box>
        ))}
      </Box>

      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650, borderCollapse: 'separate', borderSpacing: 0 }}>
          <TableHead
            sx={{
              backgroundColor: isDarkMode ? grey[800] : blue[700],
              color: isDarkMode ? 'black' : 'white',
            }}
          >
            <TableRow>
              <TableCell
                align="left"
                sx={{
                  borderLeft: '2px solid #ddd',
                  borderBottom: '2px solid #ddd',
                  fontWeight: 'bold',
                  color: 'white',
                }}
              >
                ID
              </TableCell>
              <TableCell
                align="left"
                sx={{
                  borderLeft: '2px solid #ddd',
                  borderBottom: '2px solid #ddd',
                  fontWeight: 'bold',
                  color: 'white',
                }}
              >
                VEHICULO
              </TableCell>
              <TableCell
                align="left"
                sx={{
                  borderLeft: '2px solid #ddd',
                  borderBottom: '2px solid #ddd',
                  fontWeight: 'bold',
                  color: 'white',
                }}
              >
                CLIENTE
              </TableCell>
              <TableCell
                align="left"
                sx={{
                  borderLeft: '2px solid #ddd',
                  borderBottom: '2px solid #ddd',
                  fontWeight: 'bold',
                  color: 'white',
                }}
              >
                DESDE
              </TableCell>
              <TableCell
                align="left"
                sx={{
                  borderLeft: '2px solid #ddd',
                  borderBottom: '2px solid #ddd',
                  fontWeight: 'bold',
                  color: 'white',
                }}
              >
                HASTA
              </TableCell>
              <TableCell
                align="left"
                sx={{
                  borderLeft: '2px solid #ddd',
                  borderBottom: '2px solid #ddd',
                  fontWeight: 'bold',
                  color: 'white',
                }}
              >
                PAGO
              </TableCell>
              <TableCell
                align="left"
                sx={{
                  borderLeft: '2px solid #ddd',
                  borderBottom: '2px solid #ddd',
                  fontWeight: 'bold',
                  color: 'white',
                }}
              >
                FALTAN
              </TableCell>
              <TableCell
                align="left"
                sx={{
                  borderLeft: '2px solid #ddd',
                  borderBottom: '2px solid #ddd',
                  fontWeight: 'bold',
                  color: 'white',
                }}
              >
                OBSERVACIONES
              </TableCell>
              <TableCell
                align="left"
                sx={{
                  borderLeft: '2px solid #ddd',
                  borderBottom: '2px solid #ddd',
                  fontWeight: 'bold',
                  color: 'white',
                }}
              >
                ESTADO
              </TableCell>
              <TableCell
                align="left"
                sx={{
                  borderLeft: '2px solid #ddd',
                  borderBottom: '2px solid #ddd',
                  borderRight: areUserAdmin ? null : '2px solid #ddd',
                  fontWeight: 'bold',
                  color: 'white',
                }}
              >
                USUARIO
              </TableCell>
              <TableCell
                align="center"
                sx={{
                  borderLeft: '2px solid #ddd',
                  borderBottom: '2px solid #ddd',
                  borderRight: '2px solid #ddd',
                  fontWeight: 'bold',
                  color: 'white',
                  verticalAlign: 'middle',
                }}
              >
                ACCIONES
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11}>
                  <EmptyState message={loadError ? 'No fue posible cargar las mensualidades.' : search ? 'No se encontraron resultados.' : 'No hay mensualidades para mostrar.'} />
                </TableCell>
              </TableRow>
            ) : data.map((item) => (
              <RowTableMensualidad
                key={item.id}
                d={item}
                handleDelete={handleDeleteMensualidad}
                handleUpdateData={handleUpdateData}
                areUserAdmin={areUserAdmin}
                highlighted={Number(item.id) === Number(highlightId)}
                scrollHighlight={Number(item.id) === Number(highlightId) && locatedHighlightRef.current !== Number(highlightId)}
                onHighlightLocated={handleHighlightLocated}
                searchHighlighted={searchHighlightIds.has(item.id)}
                highlightSource={createdTarget?.source}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: 2 }}>
        <Pagination
          count={totalPages}
          page={currentPage}
          onChange={handlePageChange}
          color="primary"
        />
      </Box>
    </Box>
  );
}

export default Mensualidades;
